import {useMemo} from 'react';
import {print} from 'graphql';
import gql from 'graphql-tag';
import {useQueries, useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {useHeaderBySiteId} from '@amblique/king-living/app/contentful/hooks/use-header';
import {BaseLinkFragment} from '@amblique/king-living/app/contentful/fragments/link/base-link.fragment';
import {BaseMenuItemFragment} from '@amblique/contentful/app/contentful/fragments/menu-item/base-menu-item.fragment';
import {BaseNavigationMenuFragment} from '@amblique/contentful/app/contentful/fragments/navigation-menu/base-navigation-menu.fragment';
import {BaseNavigationMenuPrimaryItemFragment} from '@amblique/contentful/app/contentful/fragments/navigation-menu-primary-item/base-navigation-menu-primary-item.fragment';
import {
  getMenuItemById,
  getNavigationMenuPrimaryItemById,
} from '@amblique/contentful/app/contentful/queries';

const getHeaderNavigationMenuById = gql`
  ${BaseMenuItemFragment}
  ${BaseNavigationMenuFragment}

  query GetHeaderNavigationMenuById($id: String!, $locale: String!, $preview: Boolean) {
    navigationMenu(id: $id, locale: $locale, preview: $preview) {
      ...BaseNavigationMenuFragment
      menuItemsCollection {
        items {
          __typename
          ... on MenuItem {
            ...BaseMenuItemFragment
          }
        }
      }
    }
  }
`;

const getHeaderNavigationMenuPrimaryItemsById = gql`
  ${BaseNavigationMenuPrimaryItemFragment}

  query GetHeaderNavigationMenuPrimaryItemsById($id: String!, $locale: String!, $preview: Boolean) {
    navigationMenu(id: $id, locale: $locale, preview: $preview) {
      sys {
        id
      }
      primaryItemsCollection {
        items {
          __typename
          ... on NavigationMenuPrimaryItem {
            ...BaseNavigationMenuPrimaryItemFragment
          }
        }
      }
    }
  }
`;

const getHeaderNavigationMenuPromotionalLinksById = gql`
  ${BaseLinkFragment}

  query GetHeaderNavigationMenuPromotionalLinksById($id: String!, $locale: String!, $preview: Boolean) {
    navigationMenu(id: $id, locale: $locale, preview: $preview) {
      sys {
        id
      }
      promotionalLinksCollection {
        items {
          __typename
          ... on Link {
            ...BaseLinkFragment
          }
        }
      }
    }
  }
`;

const HEADER_NAV_MENU_QUERY = print(getHeaderNavigationMenuById);
const HEADER_PRIMARY_ITEMS_QUERY = print(getHeaderNavigationMenuPrimaryItemsById);
const HEADER_PROMO_LINKS_QUERY = print(getHeaderNavigationMenuPromotionalLinksById);
const PRIMARY_ITEM_BY_ID_QUERY = print(getNavigationMenuPrimaryItemById);
const MENU_ITEM_BY_ID_QUERY = print(getMenuItemById);
const EMPTY_QUERY_VARS = '{}';

const collectionItemIds = (collection) => (collection?.items || [])
  .map((item) => item?.sys?.id)
  .filter(Boolean);

const mergeCollectionItems = (collection, itemsById) => {
  if (!collection?.items?.length) {
    return collection;
  }

  return {
    ...collection,
    items: collection.items.map((item) => itemsById.get(item?.sys?.id) || item),
  };
};

const isSettledFailedQuery = (query) => Boolean(
  query && !query.isLoading && (query.isError || (query.isFetched && !query.data))
);

/**
 * Custom hook to fetch Contentful header with nested data
 * Handles fetching Header, NavigationMenu, and Level 1 Menu Items separately
 * to avoid GraphQL query complexity errors
 *
 * @param {string} siteId - Site identifier (e.g., 'kl-au', 'kl-nz')
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether the hook is enabled
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Object} Header data and loading/error states
 */
export function useContentfulHeader(siteId, options = {}) {
  const {
    enabled = true,
    onSuccess,
    onError,
  } = options;

  // Get ContentfulService for fetching menu items
  const contentfulService = useService(ContentfulService);

  // Step 1: Fetch Header by siteId with 'default' variant to avoid query complexity error
  // This gives us the Header with Base fragments for nested items (includes sys.id)
  const {
    data: headerBase,
    error: headerError,
    isLoading: isHeaderLoading,
    isFetching: isHeaderFetching,
  } = useHeaderBySiteId(siteId, {
    variant: 'default',
    queryOptions: {
      enabled,
      retry: false,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    },
  });

  const navigationMenuId = headerBase?.mainNavigation?.sys?.id;

  // Primary Items live on a separate query: master Contentful has no
  // primaryItemsCollection, and putting that field on NavigationMenu 400s
  // the whole menu (Header 1.0 never loads). Missing field → empty list.
  const {
    data: primaryItemsCollection,
    isFetched: isPrimaryItemsFetched,
  } = useQuery({
    queryKey: [
      contentfulService?.key,
      'navigationMenu',
      'primaryItems',
      navigationMenuId,
      contentfulService?.locale,
    ],
    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      try {
        const data = await contentfulService.query(
          HEADER_PRIMARY_ITEMS_QUERY,
          {
            id: navigationMenuId,
            locale: contentfulService?.locale,
            preview: false,
          }
        );

        return data.navigationMenu?.primaryItemsCollection || {items: []};
      } catch {
        return {items: []};
      }
    },
    enabled: enabled && !!contentfulService && !!navigationMenuId,
    retry: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const hasCmsPrimaryItems = (primaryItemsCollection?.items || []).length > 0;
  // Header 2.0 does not need the legacy menuItems document — skip that round-trip.
  const isLegacyMenuEnabled = Boolean(
    enabled && contentfulService && navigationMenuId && isPrimaryItemsFetched && !hasCmsPrimaryItems
  );

  const {
    data: navigationMenu,
    isLoading: isNavigationMenuLoading,
  } = useQuery({
    queryKey: [
      contentfulService?.key,
      'navigationMenu',
      'id',
      navigationMenuId,
      'menu-items',
      contentfulService?.locale,
    ],
    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        HEADER_NAV_MENU_QUERY,
        {
          id: navigationMenuId,
          locale: contentfulService?.locale,
          preview: false,
        }
      );

      return data.navigationMenu || null;
    },
    enabled: isLegacyMenuEnabled,
    retry: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Promo lives on NavigationMenu, not on each Primary Item. Header 1.0 /
  // master has no field → do not query. Header 2.0 → fail closed.
  const {
    data: menuPromotionalLinksCollection,
  } = useQuery({
    queryKey: [
      contentfulService?.key,
      'navigationMenu',
      'promotionalLinks',
      navigationMenuId,
      contentfulService?.locale,
    ],
    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      try {
        const data = await contentfulService.query(
          HEADER_PROMO_LINKS_QUERY,
          {
            id: navigationMenuId,
            locale: contentfulService?.locale,
            preview: false,
          }
        );

        return data.navigationMenu?.promotionalLinksCollection || {items: []};
      } catch {
        return {items: []};
      }
    },
    enabled: Boolean(enabled && contentfulService && navigationMenuId && hasCmsPrimaryItems),
    retry: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Step 3: Default NavigationMenu fragment only returns Primary Item title.
  // Fetch each Primary Item so Shop / Discover / Support include menuItems.
  const primaryItemIds = useMemo(
    () => collectionItemIds(primaryItemsCollection),
    [primaryItemsCollection]
  );

  const primaryItemQueries = useQueries({
    queries: primaryItemIds.map((primaryItemId) => {
      const variables = {};

      return {
        queryKey: [
          contentfulService?.key,
          'navigationMenuPrimaryItem',
          'id',
          primaryItemId,
          'default',
          contentfulService?.locale,
          EMPTY_QUERY_VARS,
        ],
        queryFn: async () => {
          if (!contentfulService) {
            throw new Error('ContentfulService not available');
          }

          const data = await contentfulService.query(
            PRIMARY_ITEM_BY_ID_QUERY,
            {
              id: primaryItemId,
              locale: contentfulService?.locale,
              preview: false,
              ...variables,
            }
          );

          return data.navigationMenuPrimaryItem || null;
        },
        enabled: enabled && !!contentfulService && !!primaryItemId,
        retry: false,
        staleTime: 10 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        refetchOnWindowFocus: false,
      };
    }),
  });

  const primaryItemsData = useMemo(() => {
    return primaryItemQueries
      .map((query) => query.data)
      .filter(Boolean);
  }, [primaryItemQueries]);

  const isPrimaryItemsLoading = primaryItemIds.length > 0
    && primaryItemQueries.some((query) => query.isLoading);

  // Step 4: L1 submenu/gallery. Header 2.0 = Primary Item children only.
  const level1MenuItemIds = useMemo(() => {
    if (hasCmsPrimaryItems) {
      return [...new Set(primaryItemsData.flatMap((item) => collectionItemIds(item?.menuItemsCollection)))];
    }

    return collectionItemIds(navigationMenu?.menuItemsCollection);
  }, [hasCmsPrimaryItems, navigationMenu, primaryItemsData]);

  // Step 5: Fetch all level 1 menu items separately to get their submenuCollection children
  // Use queryKey structure matching useMenuItemById to enable React Query deduplication
  const menuItemsQueries = useQueries({
    queries: level1MenuItemIds.map((menuItemId) => {
      const variables = {}; // Empty variables object to match useMenuItemById structure

      return {
        queryKey: [
          contentfulService?.key,
          'menuItem',
          'id',
          menuItemId,
          'default',
          contentfulService?.locale,
          EMPTY_QUERY_VARS,
        ],
        queryFn: async () => {
          if (!contentfulService) {
            throw new Error('ContentfulService not available');
          }

          const data = await contentfulService.query(
            MENU_ITEM_BY_ID_QUERY,
            {
              id: menuItemId,
              locale: contentfulService?.locale,
              preview: false,
              ...variables,
            }
          );

          return data.menuItem || null;
        },
        enabled: enabled && !!contentfulService && !!menuItemId,
        retry: false,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 15 * 60 * 1000, // 15 minutes
        refetchOnWindowFocus: false,
      };
    }),
  });

  // Extract menu items data from queries
  const menuItemsData = useMemo(() => {
    return menuItemsQueries
      .map((query) => query.data)
      .filter(Boolean);
  }, [menuItemsQueries]);

  const isMenuItemsLoading = level1MenuItemIds.length > 0
    && menuItemsQueries.some((query) => query.isLoading);

  // Step 6: Merge Primary Items + hydrated level 1 menu items into the header.
  const contentfulHeader = useMemo(() => {
    if (!headerBase) return null;
    if (!navigationMenu && !primaryItemsCollection) return headerBase;

    const menuItemsById = new Map(menuItemsData.map((item) => [item?.sys?.id, item]));
    const primaryItemsById = new Map(primaryItemsData.map((item) => [item?.sys?.id, item]));
    const failedPrimaryIds = new Set(
      primaryItemIds.filter((_, index) => isSettledFailedQuery(primaryItemQueries[index]))
    );
    const menuPromotionalLinks = menuPromotionalLinksCollection
      || navigationMenu?.promotionalLinksCollection;
    const hydratedPrimaryItems = (primaryItemsCollection?.items || [])
      .filter((item) => !failedPrimaryIds.has(item?.sys?.id))
      .map((item) => {
        const fetchedPrimary = primaryItemsById.get(item?.sys?.id) || item;
        const itemPromotionalLinks = fetchedPrimary.promotionalLinksCollection;
        const hasItemPromotionalLinks = Boolean(itemPromotionalLinks?.items?.length);

        return {
          ...fetchedPrimary,
          menuItemsCollection: mergeCollectionItems(
            fetchedPrimary.menuItemsCollection,
            menuItemsById
          ),
          promotionalLinksCollection: hasItemPromotionalLinks ?
            itemPromotionalLinks :
            (menuPromotionalLinks || itemPromotionalLinks),
        };
      });

    return {
      ...headerBase,
      mainNavigation: {
        ...headerBase.mainNavigation,
        ...navigationMenu,
        menuItemsCollection: navigationMenu ?
          mergeCollectionItems(navigationMenu.menuItemsCollection, menuItemsById) :
          headerBase.mainNavigation?.menuItemsCollection,
        promotionalLinksCollection: menuPromotionalLinks
          || navigationMenu?.promotionalLinksCollection,
        primaryItemsCollection: primaryItemsCollection ?
          {
            ...primaryItemsCollection,
            items: hydratedPrimaryItems,
          } :
          primaryItemsCollection,
      },
    };
  }, [
    headerBase,
    menuItemsData,
    menuPromotionalLinksCollection,
    navigationMenu,
    primaryItemIds,
    primaryItemQueries,
    primaryItemsCollection,
    primaryItemsData,
  ]);

  // Header 2.0: wait on Primary Items only. Header 1.0: then wait on menuItems.
  const isNavQueryLoading = isLegacyMenuEnabled && isNavigationMenuLoading;
  const isPrimaryQueryPending = Boolean(navigationMenuId) && !isPrimaryItemsFetched;
  const isShellLoading = isHeaderLoading || isPrimaryQueryPending || isNavQueryLoading;
  const isLoading = isShellLoading || isPrimaryItemsLoading || isMenuItemsLoading;

  // Calculate if header is ready (has data and not loading)
  const isReady = enabled && !!contentfulHeader && !headerError && !isLoading;

  return {
    data: contentfulHeader,
    error: headerError,
    isLoading,
    isShellLoading,
    isFetching: isHeaderFetching,
    isReady,
  };
}
